import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import VirtualList from 'react-tiny-virtual-list';
import AutoSizer from 'react-virtualized-auto-sizer';

import { TemplatePlayer } from '../../types/team';
import { Paginator } from '../Paginator';

import { ItemAssistant, VariantsAssistance } from './ItemsAssistant';

import { useIsDesktop } from '@/hooks/useIsDesktop';
import { usePlayerTemplateMap } from '@/hooks/usePlayer';
import { SelectValue } from '@/types';
import { BuenosYBaratos } from '@/types/fieldAssistance';
import { EMPTY_TEMPLATE_PLAYER, FADE_IN_OUT } from '@/utils';

interface Props extends VariantsAssistance {
  dataPlayersProps: BuenosYBaratos[];
  positionSelected: SelectValue;
}

export const BestBuyPlayerTable = ({
  dataPlayersProps,
  positionSelected,
  variant = 'DEFAULT',
}: Props) => {
  const vlistRef = useRef<VirtualList>(null);
  const playerTemplateMap = usePlayerTemplateMap();
  const { isMobile } = useIsDesktop();
  const [contentCurrentPage, setContentCurrentPage] = useState<
    TemplatePlayer[]
  >([]);

  useEffect(() => {
    if (!vlistRef.current) return;
    vlistRef.current.scrollTo(0);
  }, [positionSelected.value]);

  const fieldPlayersMap = useMemo(() => {
    const map = new Map<number, BuenosYBaratos>();
    dataPlayersProps.forEach((player) => {
      map.set(player.idJT, player);
    });
    return map;
  }, [dataPlayersProps]);

  const playersFiltered = useMemo(() => {
    const players: TemplatePlayer[] = dataPlayersProps.map((player) => {
      return playerTemplateMap.get(player.idJT) || EMPTY_TEMPLATE_PLAYER;
    });

    const filteredPlayers: TemplatePlayer[] = players.flatMap(
      (player: TemplatePlayer) => {
        const matchesPosition =
          positionSelected.value === 0 || player?.p === positionSelected.value;

        const matchesPrice = player?.cot < 3000000;

        if (matchesPosition && matchesPrice) {
          return [player];
        }
        return [];
      }
    );

    return filteredPlayers.sort((a, b) => {
      const avgA = fieldPlayersMap.get(a.id)?.promedioUltimasTresFechas || 0;
      const avgB = fieldPlayersMap.get(b.id)?.promedioUltimasTresFechas || 0;
      return avgB - avgA;
    });
  }, [
    playerTemplateMap,
    dataPlayersProps,
    positionSelected.value,
    fieldPlayersMap,
  ]);

  const getDataFieldPlayer = useCallback(
    (id: number): BuenosYBaratos | undefined => {
      return fieldPlayersMap.get(id);
    },
    [fieldPlayersMap]
  );

  if (isMobile) {
    return (
      playersFiltered &&
      playersFiltered.length > 0 && (
        <>
          <div className="flex mt-5 justify-end px-5 mb-2">
            <span className="text-sm text-gdt-gray-4 font-bold">
              Promedio últ. 3 fechas
            </span>
          </div>
          <div className="h-screen  rounded-none bg-white">
            <AutoSizer className="h-full lg:h-[1002px]">
              {({ height, width }) => {
                return (
                  <VirtualList
                    ref={vlistRef}
                    height={height}
                    itemCount={playersFiltered.length}
                    itemSize={80}
                    renderItem={({ index, style }) => {
                      const player = playersFiltered[index];
                      if (player) {
                        return (
                          <div key={index} style={style}>
                            <ItemAssistant.BuenosYBaratos
                              dataPlayerFieldAssistance={getDataFieldPlayer(
                                player.id
                              )}
                              templatePlayer={player}
                              variant={variant}
                            />
                          </div>
                        );
                      }
                    }}
                    width={width}
                  />
                );
              }}
            </AutoSizer>
          </div>
        </>
      )
    );
  }

  const handleContentCurrentPage = (players: TemplatePlayer[]) => {
    players && setContentCurrentPage(players);
  };

  if (!Array.isArray(playersFiltered) || playersFiltered.length === 0) {
    return null;
  }

  const itemsToRender =
    Array.isArray(contentCurrentPage) && contentCurrentPage.length > 0
      ? contentCurrentPage
      : playersFiltered;

  return (
    <motion.div
      key={variant}
      animate="show"
      initial="hide"
      variants={FADE_IN_OUT}
    >
      <div className="lg:h-auto mt-6 rounded-none lg:rounded-lg bg-white">
        <div className="flex justify-end px-24 pt-6 pb-3 border-b-[1px] border-b-gdt-gray-7">
          <span className="text-sm text-gdt-gray-4 font-bold">
            Promedio últ. 3 fechas
          </span>
        </div>
        {itemsToRender.map((player: TemplatePlayer, index: number) =>
          player ? (
            <ItemAssistant.BuenosYBaratos
              key={`${player.id ?? 'p'}-${index}`}
              dataPlayerFieldAssistance={getDataFieldPlayer(player.id)}
              templatePlayer={player}
              variant={variant}
            />
          ) : (
            ''
          )
        )}

        {playersFiltered.length > 10 && (
          <Paginator
            key={`buenos-baratos-${positionSelected?.label ?? 'none'}`}
            currentContentPage={handleContentCurrentPage}
            items={playersFiltered}
          />
        )}
      </div>
    </motion.div>
  );
};
